import { Avatar } from '@components/shared/avatar.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { Badge } from '@components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip';
import { cn } from '@lib/utils';
import { useState } from 'react';

type AvatarListProps = {
  altText?: string;
  className?: string;
  imageUrl?: string;
  initialText: string;
  label?: string;
  tooltip?: string;
};

type AvatarListComponentProps = {
  className?: string;
  items?: AvatarListProps[];
  label?: string;
  limit?: number;
  size?: 'lg' | 'md' | 'sm';
};

export const AvatarList: React.FC<AvatarListComponentProps> = (props) => {
  const { className, items, label, limit = 3, size = 'sm' } = props;

  const [isExpanded, setIsExpanded] = useState(false);

  if (!items || items.length === 0) return null;

  const visibleItems = isExpanded ? items : items.slice(0, limit);
  const remainingCount = items.length - limit;

  const toggleExpand = () => setIsExpanded(!isExpanded);

  return (
    <div className={cn('flex flex-col', className)}>
      <TooltipProvider delayDuration={0}>
        <div className="flex flex-wrap items-center gap-1">
          {visibleItems.map((item, index) => (
            <Tooltip key={`${item.initialText}-${index}`}>
              <TooltipTrigger asChild>
                <div>
                  <Avatar
                    altText={item.altText ?? item.initialText}
                    imageUrl={item.imageUrl}
                    initialText={item.initialText}
                    size={size}
                  />
                </div>
              </TooltipTrigger>
              {item.tooltip && (
                <TooltipContent>
                  <p>{item.tooltip}</p>
                </TooltipContent>
              )}
            </Tooltip>
          ))}
          {!isExpanded && remainingCount > 0 && (
            <Badge
              className="cursor-pointer"
              onClick={toggleExpand}
              size="sm"
              variant="secondary"
            >
              +{remainingCount}
            </Badge>
          )}
          {isExpanded && items.length > limit && (
            <Badge
              className="cursor-pointer"
              onClick={toggleExpand}
              size="sm"
              variant="secondary"
            >
              Show less
            </Badge>
          )}
        </div>
      </TooltipProvider>
      {label && <BlueLabel text={label} />}
    </div>
  );
};
